import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import BulkUploadModal from '../components/BulkUploadModal';
import axios from 'axios';
import { toast } from 'sonner';
import { downloadCSV } from '../lib/exportUtils';
import {
  TrendingUp, Search, Loader2, Download, Upload, Plus, X, Save,
  ArrowRight, Calendar, DollarSign, CheckCircle2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useActiveCompany } from '../hooks/useActiveCompany';
import { EmployeeSelect } from '../components/EmployeeAutocomplete';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SalaryRevision = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useActiveCompany();
  const [employees, setEmployees] = useState([]);
  const [structures, setStructures] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [revisions, setRevisions] = useState([]);

  const [form, setForm] = useState({
    employee_id: '', new_structure_id: '', new_ctc_annual: '',
    effective_from: '', revision_reason: 'annual_increment', auto_create_arrears: true,
  });
  const [showBulkUpload, setShowBulkUpload] = useState(false);

  const fetchCompanies = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/companies`, { withCredentials: true });
      setCompanies(data);
      setSelectedCompany((cur) => (data.length > 0 && !cur ? data[0].id : cur));
    } catch (e) { console.error(e); }
  }, [setSelectedCompany]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [empRes, strRes, revRes] = await Promise.all([
        axios.get(`${API_URL}/employees`, { withCredentials: true }),
        axios.get(`${API_URL}/payroll/salary-structures?company_id=${selectedCompany}`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/payroll/arrears?company_id=${selectedCompany}`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setEmployees(empRes.data || []);
      setStructures(strRes.data || []);
      setRevisions(revRes.data?.filter(r => r.arrear_type === 'salary_revision') || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [selectedCompany]);

  useEffect(() => { if (selectedCompany) fetchData(); }, [selectedCompany, fetchData]);



  const filteredEmps = employees.filter(e =>
    !search || `${e.personal_details?.first_name} ${e.personal_details?.last_name}`.toLowerCase().includes(search.toLowerCase())
    || e.employment_details?.employee_code?.toLowerCase().includes(search.toLowerCase())
  );

  const handleOpenRevision = (emp) => {
    setSelectedEmp(emp);
    setForm({
      employee_id: emp.id, new_structure_id: structures[0]?.id || '', new_ctc_annual: '',
      effective_from: new Date().toISOString().split('T')[0], revision_reason: 'annual_increment', auto_create_arrears: true,
    });
    setShowModal(true);
  };

  const handleSubmitRevision = async () => {
    if (!form.new_ctc_annual || !form.effective_from || !form.new_structure_id) {
      toast.error('Fill all required fields'); return;
    }
    setSaving(true);
    try {
      const payload = {
        employee_id: form.employee_id,
        company_id: selectedCompany,
        new_structure_id: form.new_structure_id,
        new_ctc_annual: parseFloat(form.new_ctc_annual),
        effective_from: form.effective_from,
        revision_reason: form.revision_reason,
        auto_create_arrears: form.auto_create_arrears,
      };
      const { data } = await axios.post(`${API_URL}/payroll/salary-revision`, payload, { withCredentials: true });
      toast.success(data.message || 'Revision processed');
      setShowModal(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Revision failed'); }
    finally { setSaving(false); }
  };

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="salary-revision-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
              <TrendingUp className="inline-block w-8 h-8 mr-2 mb-1" /> Salary Revision & Increments
            </h1>
            <p className="mt-1 text-sm text-slate-600">Process mid-month increments, annual revisions, and automatic arrear calculations</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" data-testid="salary-revision-bulk-upload-btn" onClick={() => setShowBulkUpload(true)}>
              <Upload className="w-4 h-4 mr-2" /> Bulk Upload
            </Button>
            <Button variant="outline" data-testid="revisions-export-btn" onClick={() => {
              if (revisions.length === 0) { toast.error('No revisions to export'); return; }
              downloadCSV(revisions.map(r => ({
                Employee_ID: r.employee_id, Type: r.arrear_type, Effective_From: r.effective_from,
                Effective_To: r.effective_to, Amount: r.total_arrear_amount, Status: r.status
              })), 'salary_revisions.csv');
              toast.success('Revisions exported');
            }}>
              <Download className="w-4 h-4 mr-2" /> Export
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4">
          <select value={selectedCompany} onChange={e => setSelectedCompany(e.target.value)} data-testid="revision-company-select"
            className="h-10 px-3 bg-white border border-slate-300 rounded-md text-sm">
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <div className="w-80">
            <EmployeeSelect
              companyId={selectedCompany}
              value=""
              onChange={(empId, empDoc) => {
                if (empDoc) handleOpenRevision(empDoc);
              }}
              placeholder="Quick search employee for revision…"
              data-testid="revision-search-autocomplete"
            />
          </div>
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
            <input data-testid="revision-search" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Filter grid..." className="w-full h-10 pl-10 pr-4 bg-white border border-slate-300 rounded-md text-sm" />
          </div>
        </div>

        {/* How it works */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-xs text-blue-800">
          <strong>How Mid-Month Increment Works:</strong> Select an employee, set new CTC, choose effective date. The system automatically:
          (1) Closes old CTC effective on that date, (2) Creates new CTC breakdown, (3) Calculates arrears from effective date if enabled.
          Arrears appear in the Monthly Input grid as pre-filled values.
        </div>

        {/* Recent Revisions */}
        {revisions.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Recent Salary Revisions</h3>
            <div className="space-y-2">
              {revisions.slice(0, 5).map((r, i) => (
                <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded-md text-sm" data-testid={`revision-entry-${i}`}>
                  <span className="text-slate-600">Employee: {r.employee_id?.substring(0, 8)}...</span>
                  <span className="text-slate-600">Effective: {r.effective_from}</span>
                  <span className="font-mono font-semibold text-green-600">+{(r.total_arrear_amount || 0).toLocaleString()}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${r.status === 'approved' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>{r.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Employees Grid */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full" data-testid="employees-revision-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Employee</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Code</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">DOJ</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Status</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="5" className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></td></tr>
              ) : filteredEmps.length === 0 ? (
                <tr><td colSpan="5" className="text-center py-12 text-slate-500">No employees found</td></tr>
              ) : filteredEmps.map(emp => (
                <tr key={emp.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-3 px-4 text-sm font-medium text-slate-900">
                    {emp.personal_details?.first_name} {emp.personal_details?.last_name}
                  </td>
                  <td className="py-3 px-4 text-sm font-mono text-slate-600">{emp.employment_details?.employee_code}</td>
                  <td className="py-3 px-4 text-sm text-slate-600">{emp.employment_details?.date_of_joining || 'N/A'}</td>
                  <td className="py-3 px-4 text-sm">
                    <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                      {emp.employment_details?.employment_status || 'active'}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <button data-testid={`revise-${emp.employment_details?.employee_code}`} onClick={() => handleOpenRevision(emp)}
                      className="px-3 py-1.5 bg-[#0052FF] hover:bg-[#0043D1] text-white rounded-md text-xs font-medium flex items-center gap-1 ml-auto">
                      <TrendingUp className="w-3 h-3" /> Revise Salary
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Revision Modal */}
        {showModal && selectedEmp && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="revision-modal">
            <div className="bg-white rounded-lg w-full max-w-lg flex flex-col max-h-[90vh]">
              <div className="flex items-center justify-between p-5 border-b flex-shrink-0">
                <h2 className="text-lg font-semibold">Process Salary Revision</h2>
                <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-5 space-y-4 overflow-y-auto flex-1">
                <div className="bg-slate-50 rounded-md p-3">
                  <p className="text-sm font-medium text-slate-900">{selectedEmp.personal_details?.first_name} {selectedEmp.personal_details?.last_name}</p>
                  <p className="text-xs text-slate-500">{selectedEmp.employment_details?.employee_code} | DOJ: {selectedEmp.employment_details?.date_of_joining}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">New Annual CTC *</label>
                    <input data-testid="new-ctc" type="number" value={form.new_ctc_annual}
                      onChange={e => setForm({...form, new_ctc_annual: e.target.value})}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" placeholder="600000" />
                    {form.new_ctc_annual && <p className="text-xs text-slate-500 mt-1">Monthly: {(parseFloat(form.new_ctc_annual) / 12).toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Effective From *</label>
                    <input data-testid="effective-from" type="date" value={form.effective_from}
                      onChange={e => setForm({...form, effective_from: e.target.value})}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Salary Structure *</label>
                    <select data-testid="new-structure" value={form.new_structure_id} onChange={e => setForm({...form, new_structure_id: e.target.value})}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm">
                      {structures.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Reason</label>
                    <select data-testid="revision-reason" value={form.revision_reason} onChange={e => setForm({...form, revision_reason: e.target.value})}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm">
                      <option value="annual_increment">Annual Increment</option>
                      <option value="promotion">Promotion</option>
                      <option value="market_correction">Market Correction</option>
                      <option value="role_change">Role Change</option>
                      <option value="mid_month_increment">Mid-month Increment</option>
                    </select>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="autoArrears" checked={form.auto_create_arrears} data-testid="auto-arrears-checkbox"
                    onChange={e => setForm({...form, auto_create_arrears: e.target.checked})} className="rounded border-slate-300" />
                  <label htmlFor="autoArrears" className="text-sm text-slate-700">Auto-calculate arrears from effective date</label>
                </div>
              </div>
              <div className="flex justify-end gap-2 p-5 border-t flex-shrink-0">
                <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
                <Button data-testid="submit-revision-btn" onClick={handleSubmitRevision} disabled={saving} className="bg-[#0052FF] hover:bg-[#0043D1] text-white">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />} Process Revision
                </Button>
              </div>
            </div>
          </div>
        )}

        <BulkUploadModal
          open={showBulkUpload}
          onClose={() => setShowBulkUpload(false)}
          title="Bulk Salary Revision Upload"
          description="Upload a CSV to process salary revisions for multiple employees at once."
          templateUrl="/payroll/salary-revision/bulk-upload/template"
          uploadUrl="/payroll/salary-revision/bulk-upload"
          uploadParams={{ company_id: selectedCompany }}
          templateFileName="salary_revision_bulk_template.csv"
          onUploadSuccess={() => fetchData()}
          columns={['employee_code', 'new_ctc_annual', 'effective_from', 'revision_reason', 'auto_create_arrears']}
        />
      </div>
    </MainLayout>
  );
};

export default SalaryRevision;
